import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCookies } from "react-cookie";
import HeaderProfileBtn from "../../components/Header/ProfileBtn/index";
import HeaderSupportBtn from "../../components/Header/SupportBtn/index";
import HeaderRobotsBtn from "../../components/Header/RobotsBtn/index";
import Header from "../../components/Header/index";
import {
  Briefcase,
  Users,
  StickyNote,
  CalendarDays,
  Wallet,
  Archive,
  FileDown,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Stamp,
  RefreshCcw,
  DownloadCloudIcon,
} from "lucide-react";
import {
  getOptionsHandler,
  exportBackupHandler,
  downloadBackupHandler,
  toggleSectionAction,
  resetBackupAction,
} from "../../redux/action/backup/backupAction";
import { getCasesLawyer } from "../../redux/action/cases/casesAction";
import HeroTitle from "../../components/Backup/title";
import BackupEligibilityAlert from "../../components/Backup/alert/BackupEligibilityAlert";
import BackupSectionCard from "../../components/Backup/items";
import BackupGuidanceBox from "../../components/Backup/alert/BackupGuidanceBox";
import BackupErrorAlert from "../../components/Backup/alert/BackupErrorAlert";
import StatusSuccessAlert from "../../components/Backup/alert/StatusSuccessAlert";
import BackupCooldownAlert from "../../components/Backup/alert/BackupCooldownAlert";
import ExportButton from "../../components/Backup/button/index";
import { getBaseDataLawyer } from "../../redux/action/baseData/baseAction";
import { saveBaseDataInfoToLocaleStorage } from "../../utils/base-data";

const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const toFa = (n) => String(n ?? 0).replace(/[0-9]/g, (d) => FA_DIGITS[d]);

const ALWAYS_INCLUDED = ["profile"];
const HIDDEN_KEYS = new Set(ALWAYS_INCLUDED);

const ICON_MAP = {
  profile: Stamp,
  lawsuits: Briefcase,
  contacts: Users,
  notes: StickyNote,
  events: CalendarDays,
  financials: Wallet,
  bayganis: Archive,
};
const DEFAULT_ICON = Archive;

export default function BackupPage() {
  const profileId = useSelector(
    (state) => state.baseData.info.profile?.user_id,
  );

  const caseCount = useSelector((state) => state.cases?.allCases.length);
  const loaded = useSelector((state) => state.cases.loaded);
  const backupState = useSelector((state) => state.backup);
  const dispatch = useDispatch();
  const [cookies] = useCookies(["authToken"]);

  const { phase, option, selected, errorMsg, resultFile, lastBackup } =
    backupState;
  const visibleOptions = option.filter((s) => !HIDDEN_KEYS.has(s.key));
  const blockedByCaseCount = typeof caseCount === "number" && caseCount < 5;
  const isBusy =
    phase === "loading" || phase === "generating" || phase === "downloading";

  const toggleSection = (key) => {
    if (isBusy) return;
    dispatch(toggleSectionAction(key));
  };

  const startBackup = async () => {
    if (
      selected.length === 0 ||
      blockedByCaseCount ||
      isBusy ||
      isRecentlyPerformed
    )
      return;
    const sections = [...ALWAYS_INCLUDED, ...selected];
    const filename = await dispatch(
      exportBackupHandler(cookies.authToken, profileId, sections),
    );
    if (filename) {
      await dispatch(
        downloadBackupHandler(cookies.authToken, profileId, filename),
      );
    }
  };

  const handleRetry = () => {
    if (option.length === 0) {
      dispatch(getOptionsHandler(cookies.authToken, profileId));
    } else {
      dispatch(resetBackupAction());
    }
  };

  const isRecentlyPerformed = useMemo(() => {
    if (!lastBackup) return false;
    const lastBackupTime = new Date(lastBackup).getTime();
    const now = new Date().getTime();
    const diffInHours = (now - lastBackupTime) / (1000 * 60 * 60);
    return diffInHours < 24;
  }, [lastBackup]);

  const getRemainingTime = () => {
    if (!lastBackup) return "";
    const lastBackupTime = new Date(lastBackup).getTime();
    const now = new Date().getTime();
    const diffInMs = 24 * 60 * 60 * 1000 - (now - lastBackupTime);

    if (diffInMs <= 0) return "";

    const hours = Math.floor(diffInMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));

    return `حدود ${toFa(hours)} ساعت و ${toFa(minutes)} دقیقه دیگر`;
  };

  const getCases = async () => {
    await dispatch(await getCasesLawyer(cookies.authToken));
  };

  const getPostionsFromBaseData = async () => {
    const data = await dispatch(await getBaseDataLawyer(cookies.authToken));
    if (data) {
      saveBaseDataInfoToLocaleStorage(data);
    }
  };

  useEffect(() => {
    if (profileId) {
      dispatch(getOptionsHandler(cookies.authToken, profileId));
    } else {
      getPostionsFromBaseData();
    }
    if (!loaded) {
      getCases();
    }
  }, [profileId]);

  return (
    <section className="pt-28 pb-16">
      <Header title={"پشتیبان گیری"}>
        <HeaderRobotsBtn />
        <HeaderSupportBtn />
        <HeaderProfileBtn />
      </Header>

      <div className="max-w-3xl mx-auto px-4 text-[#231F14]">
        <HeroTitle />

        {blockedByCaseCount ? (
          <BackupEligibilityAlert caseCount={caseCount} toFa={toFa} />
        ) : (
          <>
            <div className="mb-3.5">
              <h2 className="mb-1 text-[15px] font-bold">
                انتخاب بخش‌ها برای پشتیبان‌گیری
              </h2>
              <p className="text-[12.5px] text-[#7A7461]">
                بخش‌های مورد نظر خود را برای ساخت فایل پشتیبان انتخاب کنید.
              </p>
            </div>

            {phase === "loading" ? (
              <div className="mb-[18px] grid grid-cols-2 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-24 animate-pulse rounded-2xl border border-primary bg-[#9865f708]"
                  />
                ))}
              </div>
            ) : (
              <div className="mb-[18px] grid grid-cols-2 gap-3">
                {visibleOptions.map((item) => {
                  const Icon = ICON_MAP[item.key] || DEFAULT_ICON;
                  const isChecked = selected.includes(item.key);
                  return (
                    <BackupSectionCard
                      item={item}
                      Icon={Icon}
                      toggleSection={toggleSection}
                      isChecked={isChecked}
                      isBusy={isBusy}
                    />
                  );
                })}
              </div>
            )}

            <BackupGuidanceBox />

            {phase === "error" && (
              <BackupErrorAlert errorMsg={errorMsg} handleRetry={handleRetry} />
            )}

            {phase === "done" && <StatusSuccessAlert resultFile={resultFile} />}

            {isRecentlyPerformed && (
              <BackupCooldownAlert getRemainingTime={getRemainingTime} />
            )}

            <ExportButton
              selected={selected}
              isBusy={isBusy}
              isRecentlyPerformed={isRecentlyPerformed}
              startBackup={startBackup}
              phase={phase}
            />
          </>
        )}
      </div>
    </section>
  );
}
